import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { environment } from 'src/environments/environment';
import {
  FileInfo,
  SubsidyOption,
  TownshipPublicSettings,
  Voucher,
  VoucherGroup,
} from 'src/app/interfaces';
import { decodeRouteParam, encodeRouteParam, ucFirst, updateTheme } from 'src/app/globals';
import { httpsCallable } from 'firebase/functions';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { StorageReference, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import {
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  AbstractControl,
} from '@angular/forms';
import { badlyInsulatedParts } from '../enums';
import { KeyValue, NgStyle, NgTemplateOutlet, DatePipe, KeyValuePipe } from '@angular/common';
import { db, functions, storage } from '../app.component';
import { PublicFunctionsService } from '../services/public-functions.service';
import { CustomValidators } from 'angular-custom-validators';
import { fileTypeFromBlob } from 'file-type';
import { MatToolbar } from '@angular/material/toolbar';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatInput } from '@angular/material/input';
import { MatChipListbox, MatChip, MatChipRemove } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { lastValueFrom, Subscription } from 'rxjs';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-burden-of-proof-form',
  templateUrl: './burden-of-proof-form.component.html',
  styleUrls: ['./burden-of-proof-form.component.scss'],
  imports: [
    NgStyle,
    MatToolbar,
    NgTemplateOutlet,
    FormsModule,
    ReactiveFormsModule,
    MatCheckbox,
    MatButton,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatError,
    MatRadioGroup,
    MatRadioButton,
    MatInput,
    MatChipListbox,
    MatChip,
    MatChipRemove,
    MatIcon,
    MatIconButton,
    MatProgressSpinner,
    DatePipe,
    KeyValuePipe,
  ],
})
export class BurdenOfProofFormComponent implements OnInit, OnDestroy {
  readonly db = db;
  readonly functions = functions;
  readonly storage = storage;
  highContrast = signal(false);
  loaded = signal(false);
  township = signal<TownshipPublicSettings | undefined>(undefined);
  townshipId = signal<string | undefined>(undefined);
  townshipName = signal<string | undefined>(undefined);
  townshipLogo = signal<string | undefined>(undefined);
  envLogo = signal<string | undefined>(undefined);
  voucherId = signal<string | undefined>(undefined);
  voucher = signal<Voucher | undefined>(undefined);
  voucherGroup = signal<VoucherGroup | undefined>(undefined);
  postal = signal<string | undefined>(undefined);
  houseNumber = signal<string | undefined>(undefined);
  subsidyOptions = signal<SubsidyOption[]>([]);
  subsidyOptionsInVoucherGroup = signal<SubsidyOption[]>([]);
  selectedSubsidyOptions = signal<any[]>([]);
  receipts = signal<FileInfo[]>([]);
  picturesBefore = signal<FileInfo[]>([]);
  picturesDuring = signal<FileInfo[]>([]);
  picturesAfter = signal<FileInfo[]>([]);
  status = signal<string | undefined>(undefined);
  energyLabels = signal<string[]>(['A', 'B', 'C', 'D', 'E', 'F', 'G', 'Geen energielabel']);
  badlyInsulatedParts = badlyInsulatedParts;
  saving = signal(false);
  hasBadEnergyLabel = signal(false);
  allowedFileExtensions = signal<string[]>([
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/pdf',
  ]);
  infoForm: FormGroup = new FormGroup({
    acceptInfo: new FormControl(null, Validators.requiredTrue),
  });
  energyLabelForm: FormGroup = new FormGroup({
    energyLabel: new FormControl(null, [Validators.required]),
    badlyInsulatedParts: new FormControl([]),
  });
  selectedSubsidyOptionsForm: FormGroup = new FormGroup([]);
  receiptsForm: FormGroup = new FormGroup([]);
  completedForm: FormGroup = new FormGroup({
    complete: new FormControl(null, Validators.required),
  });

  ucFirst = ucFirst;
  step = signal(1);
  allowEditing = signal(true);
  private readonly subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private translate: TranslateService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    private publicFunctionsService: PublicFunctionsService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.translate.setDefaultLang(environment.language);
    this.translate.use(environment.language);

    if (localStorage.getItem('highContrast') && localStorage.getItem('highContrast') === '1') {
      this.highContrast.set(true);
    }

    this.subscriptions.add(
      this.route.params.subscribe(async (params) => {
        this.townshipName.set(params.townshipName);
        if (this.townshipName()) {
          if (params.encrypted) {
            // new link structure, contains only one encrypted param that contains the voucherId, postal and houseNumber
            const [decryptedId, postal, houseNumber] = decodeRouteParam(params.encrypted).split(
              '/',
            );
            this.postal.set(postal);
            this.houseNumber.set(houseNumber);
            await this.getInfo(encodeRouteParam(decryptedId));
          } else {
            // to maintain backward compatibility with old links, can probably be removed in about a year since there should no longer be active vouchers with old links
            this.postal.set(params.postal);
            this.houseNumber.set(params.houseNumber);
            await this.getInfo(params.encryptedId);
          }
        }
      }),
    );

    const subsidyOptionDocs = (
      await getDocs(
        query(collection(this.db, `/globalSettings/global/subsidyOptions`), orderBy('name')),
      )
    ).docs;

    const subsidyOptions = subsidyOptionDocs.map((subDoc) => {
      return {
        id: subDoc.id,
        ...(subDoc.data() as SubsidyOption),
      };
    });
    this.subsidyOptions.set(subsidyOptions);
    this.syncVoucherGroupSubsidyOptions();
  }

  formValid() {
    if (this.status() == 'Submitted') {
      return true;
    }
    switch (this.step()) {
      case 1:
        return this.infoForm.valid;
      case 2:
        return this.energyLabelForm.valid;
      case 3:
        return this.selectedSubsidyOptionsForm.valid && this.selectedSubsidyOptions().length > 0;
      case 4:
        return this.receipts().length > 0 && this.receiptsForm.valid;
      case 5:
        return this.picturesBefore().length > 0;
      case 6:
        return this.picturesDuring().length > 0;
      case 7:
        return this.picturesAfter().length > 0;
      case 8:
        return this.completedForm.value.complete;
      default:
        return false;
    }
  }

  async getInfo(encryptedId: string) {
    const townshipNameDoc = (
      await getDoc(doc(this.db, `townshipNames/${this.townshipName()}`))
    ).data();
    if (!townshipNameDoc.townshipId) {
      return;
    }
    this.townshipId.set(townshipNameDoc.townshipId);
    const townshipRef = doc(this.db, `township/${this.townshipId()}/settings/public`);
    this.township.set((await getDoc(townshipRef)).data() as TownshipPublicSettings);
    if (!this.township()) {
      return console.error('township not found');
    }
    updateTheme({
      primary: this.township().primaryColor,
      secondary: this.township().accentColor,
    });
    const recaptchaToken = await this.publicFunctionsService.getRecaptchaToken('get_voucher_info');
    const callable = httpsCallable(this.functions, 'httpGetVoucherInfo', {
      timeout: 60000,
    });
    const result = await callable({
      townshipId: this.townshipId(),
      encryptedId,
      postal: this.postal(),
      houseNumber: this.houseNumber(),
      recaptchaToken,
    });
    const resData = result.data as any;
    this.voucherId.set(resData.voucher.number);
    this.voucher.set(resData.voucher as Voucher);
    this.voucherGroup.set(resData.voucherGroup as VoucherGroup);
    if (!this.voucher()) {
      return console.error('error getting voucher');
    }
    this.syncVoucherGroupSubsidyOptions();
    if (this.voucher().burdenOfProofForm) {
      this.infoForm.controls.acceptInfo.setValue(true);
      this.voucher().burdenOfProofForm.subsidyOptions?.forEach((subsidyOption) =>
        this.updateSubsidyOption(true, subsidyOption, true),
      );
      this.picturesBefore.set(this.voucher().burdenOfProofForm.picturesBefore);
      this.picturesDuring.set(this.voucher().burdenOfProofForm.picturesDuring);
      this.picturesAfter.set(this.voucher().burdenOfProofForm.picturesAfter);
      this.voucher().burdenOfProofForm.receipts?.forEach((receipt) => {
        const receiptValue: string = receipt.receiptValue.toFixed(2).toString().replace('.', ',');
        this.receiptsForm.addControl(
          receipt.name,
          new FormControl(receiptValue, [
            CustomValidators.numberInput(false, false, 2),
            Validators.required,
          ]),
        );
      });
      this.receipts.set(this.voucher().burdenOfProofForm.receipts);
      this.energyLabelForm.patchValue(this.voucher().burdenOfProofForm);
      if (!this.energyLabelForm.value.badlyInsulatedParts) {
        this.energyLabelForm.controls.badlyInsulatedParts.setValue([]);
        this.energyLabelForm.updateValueAndValidity();
      }
      this.handleEnergyLabelChange(this.energyLabelForm.value.energyLabel);
      if (
        this.voucher().burdenOfProofForm.status === 'Submitted' ||
        this.voucher().burdenOfProofForm.status === 'Accepted' ||
        this.voucher().burdenOfProofForm.status === 'DefinitivelyRejected'
      ) {
        this.status.set('Submitted');
        this.allowEditing.set(false);
        this.completedForm.controls.complete.setValue(true);
        this.infoForm.disable();
        this.receiptsForm.disable();
        this.energyLabelForm.disable();
        this.selectedSubsidyOptionsForm.disable();
        this.completedForm.disable();
      }
    }
    this.townshipName.set(this.township().name);
    if (this.township().logoImageUrl) {
      this.townshipLogo.set(this.township().logoImageUrl);
    } else {
      if (this.township().voucherPrefix === 'groene') {
        this.townshipLogo.set('../../assets/images/groenebon.png');
      } else if (this.township().voucherPrefix === 'duurzaamwonen') {
        this.townshipLogo.set('../../assets/images/duurzaamwonen.png');
      } else {
        this.townshipLogo.set('../../assets/images/lokalebon.png');
      }
    }
    if (this.township().voucherPrefix === 'groene') {
      this.envLogo.set('../../assets/images/groenebon.png');
    } else if (this.township().voucherPrefix === 'duurzaamwonen') {
      this.envLogo.set('../../assets/images/duurzaamwonen.png');
    } else {
      this.envLogo.set('../../assets/images/lokalebon.png');
    }
    this.loaded.set(true);
  }

  private syncVoucherGroupSubsidyOptions(): void {
    const voucherGroup = this.voucherGroup();
    if (!voucherGroup?.subsidyOptions || voucherGroup.subsidyOptions.length === 0) {
      this.subsidyOptionsInVoucherGroup.set([]);
      return;
    }

    const selectedSubsidyOptions = voucherGroup.subsidyOptions
      .map((id) => this.subsidyOptions().find((subOption) => subOption.id === id))
      .filter((option): option is SubsidyOption => !!option);
    this.subsidyOptionsInVoucherGroup.set(selectedSubsidyOptions);
  }

  updateSubsidyOption(checked: boolean, option: any, existingData: boolean = false) {
    if (checked) {
      this.selectedSubsidyOptions.update((v) => [...v, option]);
      this.selectedSubsidyOptionsForm.addControl(
        `${option.name}DIY`,
        new FormControl(existingData ? option.diy : true, [Validators.required]),
      );
      if (option.name === 'Warmtepomp') {
        this.selectedSubsidyOptionsForm.addControl(
          `${option.name}MainUnit`,
          new FormControl(existingData ? option.mainUnit : '', []),
        );
        if (typeof option.code !== 'undefined') {
          this.selectedSubsidyOptionsForm.addControl(
            `${option.name}Code`,
            new FormControl(existingData ? option.code : '', [
              meldcodeValidator,
              Validators.required,
            ]),
          );
        }
        if (typeof option.secondaryUnit !== 'undefined') {
          this.selectedSubsidyOptionsForm.addControl(
            `${option.name}SecondaryUnit`,
            new FormControl(existingData ? option.secondaryUnit : '', [
              CustomValidators.numberInput(false, false, 2),
            ]),
          );
        }
      } else {
        this.selectedSubsidyOptionsForm.addControl(
          `${option.name}MainUnit`,
          new FormControl(existingData ? option.mainUnit : '', [
            Validators.required,
            CustomValidators.numberInput(false, false, 2),
          ]),
        );
        const mainUnitFormControl =
          this.selectedSubsidyOptionsForm.controls[`${option.name}MainUnit`];
        if (option.name === 'PV(T)-systeem / Zonnepanelen' || option.name === 'Zonnepanelen') {
          mainUnitFormControl.setValidators([
            Validators.required,
            CustomValidators.numberInput(false, false, -1),
          ]);
          mainUnitFormControl.updateValueAndValidity();
        } else if (option.name === 'Klushulp' || option.name === 'Schouw / Advies') {
          mainUnitFormControl.setValidators(Validators.required);
          mainUnitFormControl.updateValueAndValidity();
        }
        if (typeof option.code !== 'undefined') {
          this.selectedSubsidyOptionsForm.addControl(
            `${option.name}Code`,
            new FormControl(existingData ? option.code : '', [meldcodeValidator]),
          );
        }
        if (typeof option.secondaryUnit !== 'undefined') {
          this.selectedSubsidyOptionsForm.addControl(
            `${option.name}SecondaryUnit`,
            new FormControl(existingData ? option.secondaryUnit : '', [
              CustomValidators.numberInput(false, false, 2),
            ]),
          );
        }
      }
    } else {
      const index = this.selectedSubsidyOptions().findIndex((object) => {
        return object.id == option.id;
      });
      if (index !== -1) {
        this.selectedSubsidyOptions.update((v) => {
          const newV = [...v];
          newV.splice(
            newV.findIndex((object) => {
              return object.id == option.id;
            }),
            1,
          );
          return newV;
        });
        this.selectedSubsidyOptionsForm.removeControl(`${option.name}MainUnit`);
        this.selectedSubsidyOptionsForm.removeControl(`${option.name}Code`);
        this.selectedSubsidyOptionsForm.removeControl(`${option.name}SecondaryUnit`);
        this.selectedSubsidyOptionsForm.removeControl(`${option.name}DIY`);
      }
    }
  }

  subsidyOptionIsChecked(name: string) {
    let exists = false;
    this.selectedSubsidyOptions().forEach((option) => {
      if (option.name == name) {
        exists = true;
      }
    });
    return exists;
  }

  getSubsidyOptionFormName(option: string) {
    return `subOpt.${option}`;
  }

  getCurrencyString(number: number | null | undefined) {
    if (number === undefined || number === null || isNaN(number)) {
      return '0,00';
    }
    let newNumber: string;
    if (!number.toString().includes('.')) {
      // this isnt a decimal
      newNumber = `${number.toString()},-`;
    } else {
      newNumber = `${number.toFixed(2)}`;
      newNumber = newNumber.replace('.', ',');
    }
    return newNumber;
  }

  setHighContrast() {
    this.highContrast.update((v) => !v);
    localStorage.setItem('highContrast', this.highContrast() ? '1' : '0');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  openFileInput(htmlId: string) {
    const element: HTMLElement = document.getElementById(htmlId) as HTMLElement;
    element.click();
  }

  removeFile(file: FileInfo, type: string) {
    switch (type) {
      case 'receipts':
        this.receipts.update((v) => {
          const newV = [...v];
          newV.splice(newV.indexOf(file), 1);
          return newV;
        });
        this.receiptsForm.removeControl(file.name);
        break;
      case 'picturesBefore':
        this.picturesBefore.update((v) => {
          const newV = [...v];
          newV.splice(newV.indexOf(file), 1);
          return newV;
        });
        break;
      case 'picturesDuring':
        this.picturesDuring.update((v) => {
          const newV = [...v];
          newV.splice(newV.indexOf(file), 1);
          return newV;
        });
        break;
      case 'picturesAfter':
        this.picturesAfter.update((v) => {
          const newV = [...v];
          newV.splice(newV.indexOf(file), 1);
          return newV;
        });
        break;
    }
  }

  getFileSignal(type: 'receipts' | 'picturesBefore' | 'picturesDuring' | 'picturesAfter') {
    switch (type) {
      case 'receipts':
        return this.receipts;
      case 'picturesBefore':
        return this.picturesBefore;
      case 'picturesDuring':
        return this.picturesDuring;
      case 'picturesAfter':
        return this.picturesAfter;
    }
  }

  async selectedFiles(
    event: any,
    type: 'receipts' | 'picturesBefore' | 'picturesDuring' | 'picturesAfter',
  ) {
    const currentFiles = this.getFileSignal(type)();
    if (currentFiles.length + event.target.files.length > 5) {
      this.snackbar.open(
        `U kunt maximaal 5 ${type === 'receipts' ? 'aankoopbewijzen' : "foto's"} uploaden`,
        'X',
        {
          duration: 5000,
        },
      );
      event.target.value = null;
      return;
    }

    const allowedFileExtensions = this.allowedFileExtensions().slice();
    if (type != 'receipts') {
      allowedFileExtensions.splice(allowedFileExtensions.indexOf('application/pdf'), 1);
    }
    for (let i = 0; i < event.target.files.length; i++) {
      const file = event.target.files[i] as File;

      const validation = await this.validateFile(file);
      if (!validation.valid) {
        this.snackbar.open(validation.error, 'X', {
          duration: 10000,
        });
        event.target.value = null;
        return;
      }

      const fileInfo: FileInfo = {
        name: file.name.replace(
          /\.[a-zA-Z0-9]*/,
          file.name.match(/\.[a-zA-Z0-9]*/)[0].toLowerCase(),
        ),
        file: file,
      };

      if (allowedFileExtensions.indexOf(fileInfo.file.type) == -1) {
        this.snackbar.open(
          `De bestanden moeten ${type == 'receipts' ? 'pdf-, ' : ''}jpg-, jpeg- of png-bestanden zijn`,
          'X',
          {
            duration: 10000,
          },
        );
        event.target.value = null;
        return;
      }

      switch (type) {
        case 'receipts':
          if (this.arrayContains(this.receipts(), fileInfo)) {
            return;
          }
          this.receiptsForm.addControl(
            fileInfo.name,
            new FormControl('', [
              CustomValidators.numberInput(false, false, 2),
              Validators.required,
            ]),
          );
          this.receipts.update((v) => [...v, fileInfo]);
          break;
        case 'picturesBefore':
          if (this.arrayContains(this.picturesBefore(), fileInfo)) {
            return;
          }
          this.picturesBefore.update((v) => [
            ...v,
            {
              ...fileInfo,
              previewUrl: URL.createObjectURL(fileInfo.file),
            },
          ]);
          break;
        case 'picturesDuring':
          if (this.arrayContains(this.picturesDuring(), fileInfo)) {
            return;
          }
          this.picturesDuring.update((v) => [
            ...v,
            {
              ...fileInfo,
              previewUrl: URL.createObjectURL(fileInfo.file),
            },
          ]);
          break;
        case 'picturesAfter':
          if (this.arrayContains(this.picturesAfter(), fileInfo)) {
            return;
          }
          this.picturesAfter.update((v) => [
            ...v,
            {
              ...fileInfo,
              previewUrl: URL.createObjectURL(fileInfo.file),
            },
          ]);
          break;
      }
    }
    event.target.value = null;
  }

  arrayContains(array: FileInfo[], fileInfo: FileInfo) {
    let exists = false;
    array.forEach((e) => {
      if (e.name === fileInfo.name) {
        exists = true;
      }
    });
    return exists;
  }

  async submit(status) {
    if (this.saving()) {
      return;
    }
    if (!(await this.confirmFinalSubmitIfNeeded(status))) {
      return;
    }
    this.saving.set(true);
    const receiptsFormValues = this.receiptsForm.value;
    const subsidyOptionsFormValues = this.selectedSubsidyOptionsForm.value;
    const energyLabelFormValues = this.energyLabelForm.value;
    this.receiptsForm.disable();
    this.selectedSubsidyOptionsForm.disable();
    this.energyLabelForm.disable();
    this.completedForm.disable();
    try {
      const receipts = await this.uploadFiles(this.receipts(), 'receipts', receiptsFormValues);
      let totalReceiptValue = 0;
      receipts.forEach((receipt) => (totalReceiptValue = totalReceiptValue + receipt.receiptValue));
      this.selectedSubsidyOptions().forEach((option) => {
        option.mainUnit = subsidyOptionsFormValues[`${option.name}MainUnit`];
        option.diy = subsidyOptionsFormValues[`${option.name}DIY`];
        if (
          option.secondaryUnit ||
          option.secondaryUnit != subsidyOptionsFormValues[`${option.name}SecondaryUnit`]
        ) {
          option.secondaryUnit = subsidyOptionsFormValues[`${option.name}SecondaryUnit`];
        }
        if (
          subsidyOptionsFormValues[`${option.name}Code`] &&
          (option.code || option.code != subsidyOptionsFormValues[`${option.name}Code`])
        ) {
          option.code = subsidyOptionsFormValues[`${option.name}Code`]
            .replace(/\s+/g, '')
            .toUpperCase();
        } else {
          option.code = '';
        }
      });
      const picturesBefore = await this.uploadFiles(this.picturesBefore(), 'picturesBefore');
      const picturesDuring = await this.uploadFiles(this.picturesDuring(), 'picturesDuring');
      const picturesAfter = await this.uploadFiles(this.picturesAfter(), 'picturesAfter');
      const recaptchaToken =
        await this.publicFunctionsService.getRecaptchaToken('submit_burden_of_proof');
      const submitObj: any = {
        townshipId: this.townshipId(),
        voucherId: this.voucherId(),
        postal: this.postal(),
        houseNumber: this.houseNumber(),
        status: status,
        subsidyOptions: this.selectedSubsidyOptions(),
        receiptValue: Number(totalReceiptValue.toFixed(2)),
        receipts: receipts,
        picturesBefore,
        picturesDuring,
        picturesAfter,
        ...energyLabelFormValues,
        recaptchaToken,
      };
      const callable = httpsCallable(this.functions, 'httpSubmitBurdenOfProofForm', {
        timeout: 60000,
      });
      const result = await callable(submitObj);
      const data = result.data as any;
      if (data.message == 'error_too_late') {
        this.snackbar.open('De tijd om de bewijslast aan te leveren is verstreken.', 'X', {
          duration: 0,
        });
      } else if (data.message === 'error_cannot_edit' || data.message === 'error_invalid_status') {
        this.snackbar.open(
          ' U kunt dit formulier niet meer aanpassen, omdat het al is ingediend of goedgekeurd.',
          'X',
          {
            duration: 0,
          },
        );
      } else if (data.message == 'succeed') {
        if (status == 'Submitted') {
          this.snackbar.open(
            'Uw formulier is ingediend. U ontvangt hiervan een e-mail ter bevestiging.',
            'X',
            {
              duration: 0,
            },
          );
        } else if (status == 'Concept') {
          this.snackbar.open(
            'Uw formulier is als concept opgeslagen. U ontvangt hiervan een e-mail.',
            'X',
            {
              duration: 0,
            },
          );
          this.receiptsForm.enable();
          this.selectedSubsidyOptionsForm.enable();
          this.energyLabelForm.enable();
          this.completedForm.enable();
        }
        this.status.set(status);
      } else {
        this.snackbar.open(
          'Er is iets misgegaan bij het opslaan van uw formulier. Probeer het later nog eens.',
          'X',
          {
            duration: 0,
          },
        );
        this.receiptsForm.enable();
        this.selectedSubsidyOptionsForm.enable();
        this.energyLabelForm.enable();
        this.completedForm.enable();
      }
    } catch (e) {
      console.error(e);
      this.snackbar.open(
        'Er is iets misgegaan bij het opslaan van uw formulier. Probeer het later nog eens.',
        'X',
        {
          duration: 0,
        },
      );
      this.receiptsForm.enable();
      this.selectedSubsidyOptionsForm.enable();
      this.energyLabelForm.enable();
      this.completedForm.enable();
    } finally {
      this.saving.set(false);
    }
  }

  private async confirmFinalSubmitIfNeeded(status: string): Promise<boolean> {
    if (status !== 'Submitted' || this.voucherGroup()?.cashback) {
      return true;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Formulier verzenden',
        message:
          'Let op: nadat u de bewijslast heeft ingediend, kunt u deze bon niet meer gebruiken. Weet u zeker dat u het formulier wilt verzenden?',
        confirmText: 'Ja, verzenden',
        cancelText: 'Annuleren',
      },
    });

    return !!(await lastValueFrom(dialogRef.afterClosed()));
  }

  async uploadFiles(fileList: FileInfo[], type: string, formValues?: any) {
    const files: any[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const fileInfo: FileInfo = fileList[i];
      const saveData: any = { ...fileList[i] };
      if (!fileInfo.downloadUrl) {
        const randomFileName = `${crypto.randomUUID()}-${fileInfo.file.name}`;
        let pathRef: StorageReference;
        switch (type) {
          case 'receipts':
            pathRef = ref(
              this.storage,
              `/${this.townshipId()}/vouchers/${this.voucherId()}/receipts/${randomFileName}`,
            );
            break;
          default:
            pathRef = ref(
              this.storage,
              `/${this.townshipId()}/vouchers/${this.voucherId()}/isdeImages/${randomFileName}`,
            );
            break;
        }
        let uploadFile: File = fileInfo.file;
        if (!fileInfo.name.toLowerCase().endsWith('.pdf')) {
          uploadFile = await imageCompression(fileInfo.file, {
            maxSizeMB: 2,
            maxWidthOrHeight: 1920,
          });
        }
        await uploadBytes(pathRef, uploadFile);
        saveData.downloadUrl = await getDownloadURL(pathRef);
      }
      switch (type) {
        case 'receipts':
          saveData.receiptValue = Number(`${formValues[fileInfo.name]}`.replace(',', '.'));
          break;
        default:
          break;
      }
      delete saveData.previewUrl; // only used to display preview of the files that are going to be uploaded
      delete saveData.file; // removes file cause it's to big to send to functions
      files.push(saveData);
    }
    return files;
  }

  handleEnergyLabelChange(value) {
    const formControl = this.energyLabelForm.controls.badlyInsulatedParts;
    if (value === 'A' || value === 'B' || value === 'C' || value === 'Geen energielabel') {
      this.hasBadEnergyLabel.set(true);
      formControl.addValidators([Validators.minLength(2), Validators.required]);
    } else {
      this.hasBadEnergyLabel.set(false);
      formControl.clearValidators();
    }
    formControl.updateValueAndValidity();
  }

  handleCheckboxChange(event, value) {
    const formControl = this.energyLabelForm.controls.badlyInsulatedParts;
    if (event.checked) {
      formControl.setValue([...formControl.value, value]);
    } else {
      const values = formControl.value;
      values.splice(values.indexOf(value), 1);
      formControl.setValue(values);
    }
  }

  valueAscOrder = (a: KeyValue<string, string>, b: KeyValue<string, string>): number => {
    return a.value.localeCompare(b.value);
  };

  getValidatorErrorMessage(form: any, controlName: string, controlLabel = null) {
    if (this.hasValidatorError(form, controlName)) {
      const control = form.controls[controlName];
      const errorType = Object.keys(control.errors)[0];
      let errorMessage = '';
      switch (errorType) {
        case 'required':
          errorMessage = 'Dit veld is verplicht.';
          break;
        case 'pattern':
          const pattern = control.errors.pattern.requiredPattern;
          if (pattern == '^[a-zA-Z0-9]*$') {
            errorMessage = 'Dit veld mag alleen alfanumerieke tekens bevatten.';
          }
          break;
        case 'meldcode':
          errorMessage = 'Meldcode moet beginnen met KA gevolgd door 5 cijfers.';
          break;
        case 'numberInput':
          const labelArray = ['aantal m2', 'rd/ug waarde', 'vulgewicht (kg)', 'aankoopwaarde'];
          if (
            form.value[controlName].match('[,.]') &&
            labelArray.indexOf(controlLabel.toLowerCase()) !== -1
          ) {
            errorMessage = 'Twee decimalen achter de komma zijn verplicht.';
          } else {
            errorMessage = 'Ongeldig getal ingevoerd.';
          }
          break;
      }
      return errorMessage;
    }
  }

  fixSubsidyCode(controlName: string) {
    const control = this.selectedSubsidyOptionsForm.get(controlName);
    if (control?.value) {
      const fixedValue = control.value.replace(/\s+/g, '').toUpperCase();
      control.setValue(fixedValue, { emitEvent: false });
    }
  }

  hasValidatorError(form: any, controlName: string) {
    const control = form.controls[controlName];
    if (control && control.errors) {
      const errorCount = Object.keys(control.errors);
      return errorCount ? true : false;
    }
    return false;
  }

  async validateFile(file: File): Promise<{ valid: boolean; error?: string }> {
    // Step 1: Validate with file-type
    const fileType = await fileTypeFromBlob(file);

    if (!fileType) {
      return {
        valid: false,
        error: 'Het bestand lijkt beschadigd of is niet leesbaar. Upload een geldig bestand.',
      };
    }

    if (fileType.mime !== file.type) {
      return { valid: false, error: 'Het bestandstype komt niet overeen met de extensie.' };
    }

    // Step 2: For images, try to load them
    if (file.type.startsWith('image/')) {
      const canLoad = await this.validateImage(file);
      if (!canLoad) {
        return {
          valid: false,
          error: 'Het bestand lijkt beschadigd of is niet leesbaar. Upload een geldig bestand.',
        };
      }
    }

    return { valid: true };
  }

  validateImage(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(true);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(false);
      };

      img.src = url;
    });
  }
}

function meldcodeValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null; // Let 'required' validator handle empty values
  const regex = /^\s*[kK][aA]\s*\d{5}\s*$/;
  return regex.test(value) ? null : { meldcode: true };
}
